import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "context/AuthContext";
import { useUser } from "context/UserContext";
import { Button, TextField, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FaWhatsapp } from "react-icons/fa";

const CuentaBancaria = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, getToken } = useAuth();
  const { 
    banco: bancoContext, 
    numeroCuenta: numeroCuentaContext, 
    nombre: nombreContext, 
    profesion: profesionContext,
    isDataLoaded,
    updateLocalData 
  } = useUser();
  const [banco, setBanco] = useState('');
  const [numeroCuenta, setNumeroCuenta] = useState('');
  const [numeroCuentaConfirmacion, setNumeroCuentaConfirmacion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [errorValidacion, setErrorValidacion] = useState('');
  const [errorConfirmacion, setErrorConfirmacion] = useState('');
  const [userData, setUserData] = useState({ nombre: '', profesion: '' });
  
  // Estados para el dropdown personalizado
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const baseURL = process.env.REACT_APP_BASE_URL;

  const bancosDisponibles = [
    { nombre: "BROU", requiereSucursal: false },
    { nombre: "Banco Hipotecario del Uruguay", requiereSucursal: false },
    { nombre: "Bandes", requiereSucursal: false },
    { nombre: "Itau", requiereSucursal: false },
    { nombre: "Scotiabank", requiereSucursal: false },
    { nombre: "Santander", requiereSucursal: false },
    { nombre: "BBVA", requiereSucursal: false },
    { nombre: "HSBC", requiereSucursal: false },
    { nombre: "Heritage", requiereSucursal: false },
    { nombre: "Citibank", requiereSucursal: false },
    { nombre: "Banco de la Nación Argentina", requiereSucursal: false },
    { nombre: "PREX", requiereSucursal: false },
    { nombre: "MIDINERO", requiereSucursal: false },
    { nombre: "Mi Dinero", requiereSucursal: false },
    { nombre: "OCA Blue", requiereSucursal: false },
    { nombre: "BAPRO", requiereSucursal: false },
  ];

  const restriccionesPorBanco = (banco) => {
    switch (banco) {
      case "BROU":
        return {
          minLength: 14,
          maxLength: 14,
          helperText: "14 dígitos rellenando con 0 a la izquierda"
        };
      case "Banco Hipotecario del Uruguay":
        return {
          minLength: 10,
          maxLength: 10,
          helperText: "10 dígitos rellenando con 0 a la izquierda"
        };
      case "Citibank":
        return {
          minLength: 10,
          maxLength: 10,
          helperText: "10 dígitos rellenando con 0 a la izquierda"
        };
      case "Itau":
        return {
          minLength: 7,
          maxLength: 7,
          helperText: "7 dígitos rellenando con 0 a la izquierda"
        };
      case "Scotiabank":
        return {
          minLength: 10,
          maxLength: 10,
          helperText: "10 dígitos rellenando con 0 a la izquierda"
        };
      case "Santander":
        return {
          minLength: 16,
          maxLength: 16,
          helperText: "16 dígitos rellenando con 0 a la izquierda"
        };
      case "Banco de la Nación Argentina":
        return {
          minLength: 12,
          maxLength: 12,
          helperText: "12 dígitos rellenando con 0 a la izquierda"
        };
      case "BBVA":
        return {
          minLength: 9,
          maxLength: 9,
          helperText: "9 dígitos rellenando con 0 a la izquierda"
        };
      case "HSBC":
        return {
          minLength: 10,
          maxLength: 10,
          helperText: "10 dígitos rellenando con 0 a la izquierda"
        };
      case "Heritage":
        return {
          minLength: 9,
          maxLength: 9,
          helperText: "9 dígitos rellenando con 0 a la izquierda"
        };
      case "PREX":
        return {
          minLength: 8,
          maxLength: 8,
          helperText: "8 dígitos rellenando con 0 a la izquierda"
        };
      case "MIDINERO":
      case "Mi Dinero":
        return {
          minLength: 7,
          maxLength: 7,
          helperText: "7 dígitos rellenando con 0 a la izquierda"
        };
      case "OCA Blue":
        return {
          minLength: 7,
          maxLength: 7,
          helperText: "7 dígitos rellenando con 0 a la izquierda"
        };
      case "BAPRO":
        return {
          minLength: 7,
          maxLength: 7,
          helperText: "7 dígitos rellenando con 0 a la izquierda"
        };
      default:
        return {
          minLength: 20,
          maxLength: 20,
          helperText: "20 dígitos rellenando con 0 a la izquierda"
        };
    }
  };
  const handleWhatsAppClick = () => {
    const phoneNumber = '59895438303'; // Número sin el símbolo +
    const userName = userData?.nombre || 'Usuario';
    const profesion = userData?.profesion || 'Profesion';
    const message = `Hola! Soy ${userName}, ${profesion}, necesito ayuda para cambiar mi numero de cuenta bancaria en PayFlex.`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
};

  // Función para validar el número de cuenta
  const validarNumeroCuenta = useCallback((numeroCuenta, banco) => {
    if (!banco || !numeroCuenta) {
      return { esValido: false, mensaje: '' };
    }

    const restricciones = restriccionesPorBanco(banco);
    const longitud = numeroCuenta.length;

    if (longitud !== restricciones.maxLength) {
      return {
        esValido: false,
        mensaje: `El número de cuenta debe tener exactamente ${restricciones.maxLength} dígitos para ${banco}`
      };
    }

    return { esValido: true, mensaje: '' };
  }, []);


  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Kickout solo si Firebase confirma que no hay usuario autenticado
    if (!authLoading && !user) {
      navigate("/authentication/sign-in");
      return;
    }
  }, [navigate, user, authLoading]);

  // Cargar datos desde el contexto cuando estén disponibles
  useEffect(() => {
    if (isDataLoaded) {
      if (bancoContext) setBanco(bancoContext);
      if (numeroCuentaContext) setNumeroCuenta(numeroCuentaContext);
      setUserData({
        nombre: nombreContext || '',
        profesion: profesionContext || ''
      });
    }
  }, [isDataLoaded, bancoContext, numeroCuentaContext, nombreContext, profesionContext]);

  // Función para completar con ceros a la izquierda
  const completarConCeros = (numero, longitudRequerida) => {
    return numero.padStart(longitudRequerida, '0');
  };

  // Función para filtrar solo números
  const filtrarSoloNumeros = (valor) => {
    return valor.replace(/[^0-9]/g, '');
  };

  // Handlers para los campos de número de cuenta
  const handleNumeroCuentaChange = (e) => {
    const valorFiltrado = filtrarSoloNumeros(e.target.value);
    setNumeroCuenta(valorFiltrado);
  };

  const handleNumeroCuentaConfirmacionChange = (e) => {
    const valorFiltrado = filtrarSoloNumeros(e.target.value);
    setNumeroCuentaConfirmacion(valorFiltrado);
  };

  // Validar número de cuenta cuando cambie (solo mostrar errores, no modificar el input)
  useEffect(() => {
    if (banco && numeroCuenta) {
      const validacion = validarNumeroCuenta(numeroCuenta, banco);
      setErrorValidacion(validacion.mensaje);
    } else {
      setErrorValidacion('');
    }
  }, [numeroCuenta, banco, validarNumeroCuenta]);

  // Validar coincidencia de números de cuenta
  useEffect(() => {
    if (numeroCuentaConfirmacion && numeroCuenta !== numeroCuentaConfirmacion) {
      setErrorConfirmacion('Los números de cuenta no coinciden');
    } else {
      setErrorConfirmacion('');
    }
  }, [numeroCuenta, numeroCuentaConfirmacion]);



  // eslint-disable-next-line no-unused-vars -- reservado para uso futuro en UI
  const _handleSaveBanco = async () => {
    if (!banco) {
      setMensaje("Por favor, selecciona un banco");
      return;
    }

    setIsLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        setMensaje("Error: No se pudo obtener el token de autenticación");
        navigate("/authentication/sign-in");
        setIsLoading(false);
        return;
      }
      
      const profesionalId = localStorage.getItem('id');
      
      await fetch(`${baseURL}/actualizar-profesional/${profesionalId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ banco }),
      });
      
      setMensaje("Banco actualizado correctamente");
    } catch (error) {
      setMensaje("Error al actualizar el banco");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCuenta = async () => {
    if (!numeroCuenta) {
      setMensaje("Por favor, ingresa el número de cuenta");
      return;
    }

    if (!numeroCuentaConfirmacion) {
      setMensaje("Por favor, confirma el número de cuenta");
      return;
    }

    if (numeroCuenta !== numeroCuentaConfirmacion) {
      setMensaje("Los números de cuenta no coinciden");
      return;
    }

    // Completar con ceros a la izquierda antes de guardar
    const restricciones = restriccionesPorBanco(banco);
    const numeroCompletado = completarConCeros(numeroCuenta, restricciones.maxLength);

    setIsLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        setMensaje("Error: No se pudo obtener el token de autenticación");
        navigate("/authentication/sign-in");
        setIsLoading(false);
        return;
      }
      
      const profesionalId = localStorage.getItem('id');
      
      await fetch(`${baseURL}/actualizar-profesional/${profesionalId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ banco, numeroCuenta: numeroCompletado }),
      });
      
      // Actualizar el contexto local
      updateLocalData({ banco, numeroCuenta: numeroCompletado });
      
      // Actualizar el estado con el número completado
      setNumeroCuenta(numeroCompletado);
      setNumeroCuentaConfirmacion(numeroCompletado);
      setMensaje("Número de cuenta actualizado correctamente");
      setShowConfirmModal(false);
    } catch (error) {
      setMensaje("Error al actualizar el número de cuenta");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(true);
  };

  const handleCancelSave = () => {
    setShowConfirmModal(false);
  };

  const getRestriccionesBanco = () => {
    if (!banco) return null;
    return restriccionesPorBanco(banco);
  };

  const restricciones = getRestriccionesBanco();
  const validacion = validarNumeroCuenta(numeroCuenta, banco);
  const numerosCoinciden = numeroCuenta === numeroCuentaConfirmacion;
  const puedeGuardar = numeroCuenta && numeroCuentaConfirmacion && banco && validacion.esValido && numerosCoinciden;

  // Funciones para el dropdown personalizado
  const handleBancoSelect = (bancoSeleccionado) => {
    setBanco(bancoSeleccionado);
    setNumeroCuenta(''); // Resetear el número de cuenta al cambiar de banco
    setNumeroCuentaConfirmacion(''); // Resetear también la confirmación
    setDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'white',
      padding: '20px',
      paddingBottom: '100px', // Espacio para el BottomTabBar (80px + 20px extra)
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '0px',
        position: 'relative'
      }}>
        <IconButton 
          onClick={() => navigate('/perfil')}
          sx={{ 
            color: "#000",
            padding: '8px',
            marginRight: '16px'
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <h1 style={{ 
          fontWeight: 'bold',
          color: '#000',
          fontSize: '20px',
          margin: 0,
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          Cuenta bancaria
        </h1>
      </div>

      {/* Contenedor centrado para la card */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: 'calc(100vh - 200px)', // Altura menos el espacio del header y bottom bar
        paddingTop: '1vh' // Mucho más arriba del centro
      }}>
        {/* Mensaje de estado */}
        {mensaje && (
          <div style={{
            backgroundColor: mensaje.includes('Error') ? '#ffebee' : '#e8f5e8',
            color: mensaje.includes('Error') ? '#c62828' : '#2e7d32',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            textAlign: 'center',
            width: '100%',
            maxWidth: '400px'
          }}>
            {mensaje}
          </div>
        )}

        {/* Card principal */}
        <div style={{ 
          width: '100%', 
          maxWidth: '400px',
          borderRadius: '20px', 
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)', 
          backgroundColor: 'white',
          overflow: 'visible',
          padding: '20px',
          position: 'relative',
          zIndex: 1
        }}>
        {/* Selección de banco */}
        <div style={{ marginBottom: '24px' }}>
          <Typography variant="h6" sx={{ color: '#000', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
            Seleccione su banco
          </Typography>
          
          {/* Dropdown personalizado */}
          <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
            <div
              onClick={toggleDropdown}
              style={{
                width: '100%',
                minHeight: '48px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                padding: '12px 14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease',
                marginBottom: '12px',
                '&:hover': {
                  borderColor: '#1F85F6'
                }
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#1F85F6';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e0e0e0';
              }}
            >
              <span style={{ 
                color: banco ? '#000' : '#999', 
                fontSize: '15px',
                flex: 1
              }}>
                {banco || 'Seleccione su banco'}
              </span>
              <KeyboardArrowDownIcon 
                style={{ 
                  color: '#666', 
                  fontSize: '24px',
                  transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }} 
              />
            </div>

            {/* Lista de opciones */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                zIndex: 9999,
                maxHeight: 'min(500px, 45vh)',
                overflowY: 'auto',
                marginTop: '4px'
              }}>
                {bancosDisponibles.map((bancoItem) => (
                  <div
                    key={bancoItem.nombre}
                    onClick={() => handleBancoSelect(bancoItem.nombre)}
                    style={{
                      padding: '10px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: '15px',
                      color: '#000',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f8f9fa'
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                    }}
                  >
                    {bancoItem.nombre}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Número de cuenta */}
        <div style={{ marginBottom: '24px' }}>
          <Typography variant="h6" sx={{ color: '#000', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
            Número de cuenta
          </Typography>
          <TextField
            fullWidth
            value={numeroCuenta}
            onChange={handleNumeroCuentaChange}
            placeholder="Número de cuenta"
            variant="outlined"
            sx={{
              marginBottom: '12px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#f8f9fa'
              },
              '& .MuiOutlinedInput-input': {
                paddingLeft: '0px',
                paddingRight: '8px',
                fontSize: '14px',
                width: '100% !important',
                maxWidth: 'none !important',
                boxSizing: 'border-box'
              }
            }}
            inputProps={{
              maxLength: restricciones?.maxLength || 20
            }}
          />
          
          {/* Mensaje de ayuda para el formato */}
          {restricciones && (
            <Typography variant="caption" sx={{ 
              color: '#666', 
              fontSize: '12px', 
              display: 'block', 
              marginBottom: '8px',
              fontStyle: 'italic'
            }}>
              {restricciones.helperText}
            </Typography>
          )}

          {/* Mensaje de error de validación */}
          {errorValidacion && (
            <Typography variant="caption" sx={{ 
              color: '#d32f2f', 
              fontSize: '12px', 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: 'medium'
            }}>
              {errorValidacion}
            </Typography>
          )}

          {/* Campo de confirmación del número de cuenta */}
          <Typography variant="h6" sx={{ color: '#000', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', marginTop: '16px' }}>
            Confirmar número de cuenta
          </Typography>
          <TextField
            fullWidth
            value={numeroCuentaConfirmacion}
            onChange={handleNumeroCuentaConfirmacionChange}
            placeholder="Confirme el número de cuenta"
            variant="outlined"
            sx={{
              marginBottom: '12px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#f8f9fa'
              },
              '& .MuiOutlinedInput-input': {
                paddingLeft: '0px',
                paddingRight: '8px',
                fontSize: '14px',
                width: '100% !important',
                maxWidth: 'none !important',
                boxSizing: 'border-box'
              },
              '& .MuiInputBase-input::placeholder': {
                fontSize: '14px',
                opacity: 0.65
              }
            }}
            inputProps={{
              maxLength: restricciones?.maxLength || 20
            }}
          />


          {/* Mensaje de error de confirmación */}
          {errorConfirmacion && (
            <Typography variant="caption" sx={{ 
              color: '#d32f2f', 
              fontSize: '12px', 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: 'medium'
            }}>
              {errorConfirmacion}
            </Typography>
          )}


          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirmSave}
            disabled={isLoading || !puedeGuardar}
            sx={{
              backgroundColor: '#1F85F6 !important',
              color: 'white !important',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 'bold',
              padding: '12px',
              '&:hover': {
                backgroundColor: '#1F85F6 !important'
              },
              '&:disabled': {
                backgroundColor: '#ccc !important',
                color: '#666 !important'
              }
            }}
          >
            Guardar
          </Button>
        </div>
        <div className="HelpSection">
                                <button 
                                    className="WhatsAppHelpButton"
                                    onClick={handleWhatsAppClick}
                                >
                                    <FaWhatsapp size={20} style={{ backgroundColor: '#25D366', borderRadius: '50%', padding: '4px', color: '#fff' }} />
                                    <span style={{ fontSize: '16px' }}>¿Necesitas ayuda?</span>
                                </button>
                            </div>
        </div>
      </div>

      {/* Modal de confirmación */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }}>
            <Typography variant="h6" sx={{ 
              color: '#000', 
              fontSize: '18px', 
              fontWeight: 'bold', 
              marginBottom: '16px' 
            }}>
              Confirmar guardado
            </Typography>
            
            <Typography variant="body1" sx={{ 
              color: '#666', 
              fontSize: '14px', 
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              ¿Estás seguro de que quieres guardar el número de cuenta <strong>{numeroCuenta.length < (restricciones?.maxLength || 20) ? completarConCeros(numeroCuenta, restricciones?.maxLength || 20) : numeroCuenta}</strong> para <strong>{banco}</strong>?
              <br /><br />
              Ambos campos coinciden correctamente.
            </Typography>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <Button
                variant="contained"
                onClick={handleSaveCuenta}
                disabled={isLoading}
                sx={{
                  backgroundColor: '#1F85F6 !important',
                  color: 'white !important',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  '&:hover': {
                    backgroundColor: '#1F85F6 !important'
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc !important',
                    color: '#666 !important'
                  }
                }}
              >
                {isLoading ? 'Guardando...' : 'Confirmar'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancelSave}
                disabled={isLoading}
                sx={{
                  borderColor: '#ccc',
                  color: '#666',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  '&:hover': {
                    borderColor: '#999',
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Estilos CSS para la sección de ayuda
const styles = `
/* Estilos para la sección de ayuda en el modal */
.HelpSection {
    margin-top: 26px;
    margin-right: 0;
    margin-bottom: 5px;
    margin-left: 0;
    padding: 0;
    background-color: transparent;
    text-align: center;
    border-top: none;
}

.HelpText {
    margin: 0 0 5px 0;
    font-size: 11px;
    color: #ccc;
    font-family: 'Helvetica Neue', sans-serif;
    font-weight: 300;
}

.WhatsAppHelpButton {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background-color: transparent;
    color: #25D366;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 11px;
    font-family: 'Helvetica Neue', sans-serif;
    font-weight: 300;
    transition: color 0.2s ease;
    text-decoration: none;
    text-decoration-color: transparent;
}

.WhatsAppHelpButton span {
    font-size: 11px;
}
`;

// Agregar estilos al DOM
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
}

export default CuentaBancaria;